import { format } from "date-fns";
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { FILTER_SHIFT_PATTERN_KEY, FILTER_WORK_CENTERS_KEY } from "../../../assets/constants";
import {
  SHIFT_PATTERNS,
  WORK_CENTERS,
} from "../../../components/filters/constants";
import { FULL_TIME_SCHEDULER_PERMISSIONS } from "../../../config/permissions";
import { hasPermissionString } from "../../../utils/helpers";
import { Filter } from "../../filters/filter-accordion/types";
import { FilterContext } from "../../filters/filter-context";
import {
  fetchRequestTypes,
} from "../../full-time-worker-scheduler/store/worker-scheduler-slice";
import { getDayNumber } from "../../scheduler/ui/dialog/copy-shifts-dialog/utils";
import { resetPostShiftDemandStatus } from "../../shift-demand/store/shift-demand-slice";
import {
  getFullTimeShifts,
  getFullTimeShiftsFilters, getLatestSchedulingStatus,
  getRequestStatusTypes,
  resetShiftsMeta
} from "../store/full-time-shifts-slice";
import { ShiftMeta } from "../store/full-time-shifts-type";
import useFullTimeShiftsData from "./use-full-time-shifts-data";

const useFullTimeShiftsController = () => {
  const dispatch = useDispatch();
  const {
    currentDate,
    weekStartDay,
    filterData,
    getFullTimeShiftsFiltersStatus,
    fullTimeShifts,
    selectedLocation,
    shiftMetaList,
    jobs,
    permissions,
    saveOverridesStatus,
    FTScheduleGenerationEnabledLocations,
  } = useFullTimeShiftsData();
  const todayDateRange = useMemo(
    () => {
      return {
        startDate: startOfWeek(
          new Date(
            new Date().setHours(0, 0, 0, 0)
          ),
          {
            weekStartsOn: getDayNumber(weekStartDay)
          }
        ),
        endDate: addDays(
          startOfWeek(
            new Date(
              new Date().setHours(0, 0, 0, 0)
            ),
            {
              weekStartsOn: getDayNumber(weekStartDay)
            }
          ),
          7
        )
      }},
    [weekStartDay]
  );

  const dateRange = useMemo(
    () => {
      if (!currentDate) {
        return todayDateRange;
      }
      return {
        startDate: startOfWeek(
          new Date(
            new Date(currentDate).setHours(0, 0, 0, 0)
          ),
          {
            weekStartsOn: getDayNumber(weekStartDay)
          }
        ),
        endDate: addDays(
          startOfWeek(
            new Date(
              new Date(currentDate).setHours(0, 0, 0, 0)
            ),
            {
              weekStartsOn: getDayNumber(weekStartDay)
            }
          ),
          7
        )
      }},
    [currentDate, weekStartDay, todayDateRange]
  );
  const shiftMetaMap = useMemo(() => shiftMetaList.reduce(
    (acc: { [x: string]: any }, meta: { shiftId: string | number , workcenterId: number}) => {
      acc[`${meta.shiftId}_${meta.workcenterId}`] = meta;
      return acc;
    },
    {} as Record<string, ShiftMeta>
  ), [shiftMetaList]);
  const jobsMap = useMemo(() => jobs.reduce(
    (acc: { [x: string]: any }, job: { id: string | number, name: string}) => {
      acc[job.id] = job.name;
      return acc;
    },
    {} as Record<number, string>
  ), [jobs]);
  const workcentersMap = useMemo(() => {
    return filterData.allWorkcenters.reduce(
      (acc: { [x: string]: any }, workcenter: { id: number, name: string }) => {
        acc[workcenter.id] = workcenter.name;
        return acc;
      },
      {} as Record<number, string>
    );
  }, [filterData?.allWorkcenters]);
  const hasFetchedRef = useRef(false);
  const [shiftsLoadedForDate, setShiftsLoadedForDate] = useState("");
  const filterContext = useContext(FilterContext);
  if (!filterContext) {
    throw new Error("FilterContext must be used within a FilterProvider");
  }
  const { appliedFilters, open, filters, setFilterOpen, setAppliedFilters, setFilters, setDataRelations } = filterContext;

  useEffect(() => {
    try {
      // Only for initial shift loading
      const startDateISO = dateRange.startDate.toISOString();
      if (!hasFetchedRef.current && shiftsLoadedForDate !== startDateISO) {
        hasFetchedRef.current = true;
        setShiftsLoadedForDate(startDateISO);
        dispatch(
          getFullTimeShifts({
            startDate: startDateISO,
            endDate: dateRange.endDate.toISOString(),
          })
        );
        dispatch(
          getFullTimeShiftsFilters()
        );
        dispatch(
          fetchRequestTypes()
        );
        dispatch(
          getRequestStatusTypes()
        );
      }
    } catch (e) {
      // Do nothing
    }
  }, [dispatch, dateRange, shiftsLoadedForDate]);

  useEffect(() => {
    if (saveOverridesStatus === "fulfilled") {
      dispatch(resetShiftsMeta());
      dispatch(getFullTimeShifts({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString()
      }));
    }
  }, [dateRange.endDate, dateRange.startDate, dispatch, saveOverridesStatus]);

  useEffect(() => {
    if (getFullTimeShiftsFiltersStatus === "fulfilled") {
      const {
        allWorkcenters,
        shiftPatterns,
        shiftPatternWorkCenter,
        filterRelationships,
      } = filterData;

      setAppliedFilters([]);
      const newFilters: Filter[] = [];
      const selectedLocationWorkcenters = allWorkcenters?.filter(
        (workcenter) => workcenter.locationId === selectedLocation
      )?.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
      if (selectedLocationWorkcenters?.length > 0) {
        newFilters.push({
          id: 1,
          name: WORK_CENTERS,
          key: FILTER_WORK_CENTERS_KEY,
          data: selectedLocationWorkcenters,
          reducedData: selectedLocationWorkcenters,
          searchedData: selectedLocationWorkcenters
        });
        const allFilterWorkCentersIds = selectedLocationWorkcenters.map(
          (workcenter) => workcenter.id
        );
        const newShiftPatterns = shiftPatterns?.filter(
          (pattern) =>
            shiftPatternWorkCenter?.some(
              (item) => item.patternId === pattern.id &&
                item.workCenterIds.split(",").map((id: string) => Number(id.trim())).some((id: number) =>
                  allFilterWorkCentersIds.includes(id)
                )
            )
        )?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        }) || [];
        if (newShiftPatterns?.length > 0) {
          newFilters.push({
            id: 2,
            name: SHIFT_PATTERNS,
            key: FILTER_SHIFT_PATTERN_KEY,
            data: newShiftPatterns,
            reducedData: newShiftPatterns,
            searchedData: newShiftPatterns
          });
        }
      }
      setFilters(newFilters);
      setDataRelations(filterRelationships);
    }
  }, [filterData, getFullTimeShiftsFiltersStatus, selectedLocation, setAppliedFilters, setDataRelations, setFilters]);

  useEffect(() => {
    const isScheduleGenerationEnabled = FTScheduleGenerationEnabledLocations?.includes(selectedLocation ?? 0);
    if (selectedLocation
      && isScheduleGenerationEnabled
      && hasPermissionString(permissions,
        FULL_TIME_SCHEDULER_PERMISSIONS.GENERATE_SCHEDULE)) {
      dispatch(getLatestSchedulingStatus({
        locationId: selectedLocation,
        fromDate: format(dateRange.startDate, "yyyy-MM-dd"),
        toDate: format(addDays(dateRange.startDate, 6), "yyyy-MM-dd")
      }));
    }
  }, [dateRange, dispatch, selectedLocation, permissions, FTScheduleGenerationEnabledLocations]);

  const getRangeForSelectedDate = (selectedDate: Date) => {
    const startDate = startOfWeek(
      new Date(selectedDate.setHours(0, 0, 0, 0)),
      {
        weekStartsOn: getDayNumber(weekStartDay)
      }
    );
    const endDate = addDays(startDate, 7);
    return {
      startDate,
      endDate
    };
  };

  const reducedWorkCenters = useMemo(() => {
    if (!filters || filters.length === 0) {
      return [];
    }
    const workCentersFilter = filters.find(
      (filter) => filter.name === WORK_CENTERS
    );
    return workCentersFilter ? workCentersFilter.data : [];
  },[filters]);

  const filteredWorkCenters = useMemo(() => {
    if (!reducedWorkCenters || reducedWorkCenters.length === 0) {
      return [];
    }
    const appliedWorkCenters = appliedFilters.find(
      (filter) => filter.type === WORK_CENTERS
    )?.data || [];
    const appliedWorkCenterIds = appliedWorkCenters.map(
      (workcenter) => Number(workcenter.id)
    );
    return appliedWorkCenterIds.length > 0
      ? reducedWorkCenters.filter((workcenter) =>
          appliedWorkCenterIds.includes(workcenter.id)
        )
      : reducedWorkCenters;
  },[appliedFilters, reducedWorkCenters])

  const reducedFullTimeShifts = useMemo(() => {
    if (!fullTimeShifts || fullTimeShifts.length === 0) {
      return [];
    }
    return fullTimeShifts.filter((shift) => {
      const filtersShiftPatternsIndex = filters.findIndex(
        (filter) => filter.name === SHIFT_PATTERNS
      ) ?? -1;
      const filtersShiftPatterns = filtersShiftPatternsIndex >= 0 ?
        filters[filtersShiftPatternsIndex].data : [];
      const shiftPatternIds = filtersShiftPatterns.map(
        (pattern) => pattern.id
      );
      return shiftPatternIds.includes(shift.shiftPatternId);
    });
  },[filters, fullTimeShifts])

  const filteredFullTimeShifts = useMemo(() => {
    if (!reducedFullTimeShifts || reducedFullTimeShifts.length === 0) {
      return [];
    } else {
      const appliedWorkCenters = appliedFilters.find(
        (filter) => filter.type === WORK_CENTERS
      )?.data || [];
      const appliedShiftPatterns = appliedFilters.find(
        (filter) => filter.type === SHIFT_PATTERNS
      )?.data || [];
      const appliedWorkCenterIds = appliedWorkCenters.map(
        (workcenter) => Number(workcenter.id)
      );
      const appliedShiftPatternIds = appliedShiftPatterns.map(
        (pattern) => pattern.id
      );
      const fullTimeShiftsFilteredByWorkCenter = appliedWorkCenterIds.length > 0
        ? reducedFullTimeShifts.filter((shift) =>
            appliedWorkCenterIds.includes(shift.workcenterId)
          )
        : reducedFullTimeShifts;
      return appliedShiftPatternIds.length > 0
        ? fullTimeShiftsFilteredByWorkCenter.filter((shift) =>
            appliedShiftPatternIds.includes(shift.shiftPatternId)
          )
        : fullTimeShiftsFilteredByWorkCenter;
    }
  }, [appliedFilters, reducedFullTimeShifts])

  useEffect(() => {
      // close snackbar if opened already
      dispatch(resetPostShiftDemandStatus());
    },[dispatch])

  return [
    {
      appliedFilters,
      filters,
      reducedWorkCenters,
      filteredWorkCenters,
      isFilterOpen: open,
      todayDateRange,
      dateRange,
      shiftMetaMap,
      filteredFullTimeShifts,
      jobsMap,
      workcentersMap,
    },
    {
      dispatch,
      setIsFilterOpen: setFilterOpen,
      getRangeForSelectedDate,
      setFilters
    }
  ] as const;
}

export default useFullTimeShiftsController;